import { useState, useEffect, useCallback, useRef } from "react";
import { stravaService } from "../services/StravaService";

export function useStravaStatus() {
  const [isConnected, setIsConnected] = useState(false);
  const [athleteId, setAthleteId] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  const popupRef = useRef(null);
  const pollTimerRef = useRef(null);

  const checkStatus = useCallback(async () => {
    const token = sessionStorage.getItem("brv-user-token");
    if (!token) {
      setIsConnected(false);
      setAthleteId(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await stravaService.getStatus();
      setIsConnected(Boolean(data.conectado));
      setAthleteId(data.strava_athlete_id || null);
      setUpdatedAt(data.atualizado_em || null);
    } catch (err) {
      console.error("Erro ao verificar status do Strava:", err);
      setError(err.message || "Erro ao verificar conexão com o Strava");
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Limpeza de timers/ouvintes ao desmontar
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  const connect = useCallback(async (options = {}) => {
    try {
      setIsConnecting(true);
      setError(null);

      const authUrl = await stravaService.getConnectUrl();
      if (!authUrl) {
        throw new Error("Não foi possível obter a URL de autorização.");
      }

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        "strava_oauth_popup",
        `width=${width},height=${height},left=${left},top=${top},status=no,menubar=no,toolbar=no`
      );

      popupRef.current = popup;

      if (!popup) {
        // Bloqueador de popup ativado: fallback para redirecionamento
        window.location.href = authUrl;
        return;
      }

      return new Promise((resolve, reject) => {
        const handleMessage = async (event) => {
          if (
            event.data &&
            (event.data.type === "STRAVA_AUTH_SUCCESS" ||
              event.data === "STRAVA_AUTH_SUCCESS")
          ) {
            window.removeEventListener("message", handleMessage);
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            if (popupRef.current && !popupRef.current.closed) {
              popupRef.current.close();
            }

            await checkStatus();
            setIsConnecting(false);
            if (options.onSuccess) options.onSuccess();
            resolve(true);
          }
        };

        window.addEventListener("message", handleMessage);

        // Monitora fechamento manual da janela pelo usuário
        pollTimerRef.current = setInterval(() => {
          if (popup.closed) {
            clearInterval(pollTimerRef.current);
            window.removeEventListener("message", handleMessage);
            setIsConnecting(false);
            // Faz uma verificação final caso o callback tenha salvado mas o postMessage falhou
            checkStatus().then(() => resolve(false));
          }
        }, 1000);
      });
    } catch (err) {
      console.error("Erro ao iniciar conexão com Strava:", err);
      setError(err.message || "Erro ao conectar com Strava");
      setIsConnecting(false);
      if (options.onError) options.onError(err);
      throw err;
    }
  }, [checkStatus]);

  const disconnect = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      setError(null);
      await stravaService.desconectar();
      setIsConnected(false);
      setAthleteId(null);
      setUpdatedAt(null);
      if (options.onSuccess) options.onSuccess();
    } catch (err) {
      console.error("Erro ao desconectar do Strava:", err);
      setError(err.message || "Erro ao desconectar");
      if (options.onError) options.onError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    isConnected,
    athleteId,
    updatedAt,
    loading,
    isConnecting,
    error,
    checkStatus,
    connect,
    disconnect,
  };
}

export default useStravaStatus;
